// src/components/PromoOffer.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import NewCustomerPromotionForm from "./newCustomerPromoForm";
import PromoImage2 from "../assets/images/promotions/sexy-couple-at-home-1200x800.jpg";
import PromoImage1 from "../assets/images/promotions/young-african-american-couple-with-water-drops-1200x800.jpg";
import PromoImage3 from "../assets/images/promotions/african-american-man-in-elegant-suit-looking-1200x800.jpg";

const PROMO_IMAGES = [PromoImage1, PromoImage2, PromoImage3];

const shuffleArray = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const PromoOffer = ({
  tabText = "Get 20% Off",
  imageSrc, // optional override
  imageAlt = "Promotion",
  open: controlledOpen,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const imageBagRef = useRef(shuffleArray(PROMO_IMAGES));
  const preloadIdRef = useRef(0);

  const getNextShuffledImage = () => {
    if (PROMO_IMAGES.length === 0) return null;
    if (PROMO_IMAGES.length === 1) return PROMO_IMAGES[0];

    if (imageBagRef.current.length === 0) {
      imageBagRef.current = shuffleArray(PROMO_IMAGES);
    }

    return imageBagRef.current.pop();
  };

  const [randomImage, setRandomImage] = useState(null);
  const [isImageReady, setIsImageReady] = useState(false);

  const preloadImage = (src, onReady) => {
    if (!src) return;

    const requestId = ++preloadIdRef.current;
    const img = new Image();

    img.onload = () => {
      if (preloadIdRef.current !== requestId) return;
      onReady(src);
    };

    img.onerror = () => {
      if (preloadIdRef.current !== requestId) return;
      onReady(src);
    };

    img.src = src;
  };

  // Preload the initial image once
  useEffect(() => {
    if (imageSrc) {
      preloadImage(imageSrc, (loadedSrc) => {
        setRandomImage(loadedSrc);
        setIsImageReady(true);
      });
      return;
    }

    const firstImage = getNextShuffledImage();
    preloadImage(firstImage, (loadedSrc) => {
      setRandomImage(loadedSrc);
      setIsImageReady(true);
    });
  }, [imageSrc]);

  // When modal opens, preload the image first, then reveal the modal content
  useEffect(() => {
    if (!open) {
      setIsImageReady(false);
      return;
    }

    const nextImage = imageSrc ?? getNextShuffledImage();
    setIsImageReady(false);

    preloadImage(nextImage, (loadedSrc) => {
      setRandomImage(loadedSrc);
      setIsImageReady(true);
    });
  }, [open, imageSrc]);

  const finalImageSrc = imageSrc ?? randomImage;

  // Lock body scroll + ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);

    if (open) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    } else {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    }

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, setOpen]);

  return (
    <>
      <button
        type="button"
        className="promo-tab"
        aria-label={tabText}
        onClick={() => setOpen(true)}
      >
        {tabText}
      </button>

      {open && isImageReady && finalImageSrc &&
        createPortal(
          <div
            className="promo-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Promotion sign-up"
            onClick={() => setOpen(false)}
          >
            {isImageReady && finalImageSrc ? (
              <div
                className="promo-modal__content"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="promo-modal__close"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                >
                  ×
                </button>

                <div className="promo-modal__left">
                  <div className="promo-modal__left-inner">
                    <h2 className="mb-3 fw-bold">Claim Your Welcome Discount</h2>
                    <p className="text-muted mb-4">
                      New here? Pop in your details below and grab your first-time
                      code.
                    </p>
                    <NewCustomerPromotionForm />
                  </div>
                </div>

                <div className="promo-modal__right" aria-hidden="true">
                  <img src={finalImageSrc} alt={imageAlt} />
                </div>
              </div>
            ) : (
              <div
                className="promo-modal__loading"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default PromoOffer;